import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import axios from 'axios';
import ReactTooltip from 'react-tooltip';
import { LoaderSpinner } from './LoaderSpinner';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { ROUTES } from '../consts/general';

const getEdenBaseURL = () => {
  const env = process.env.REACT_APP_KL_ENV;
  if (env === "beta") return "https://kingsloot-beta-qgqri.ondigitalocean.app";
  if (env === "prod") return "https://eden.kingsloot.com";
  return window.location.origin;
};

// Normalize PO number to extract just the numeric part
// Handles formats like: XXX, PO XXX, POXXX, PO XXX
const normalizePONumber = (poNumber) => {
  if (!poNumber) return '';
  const str = String(poNumber).trim();
  // Remove "PO" prefix (case-insensitive, with optional spaces)
  const cleaned = str.replace(/^po\s*/i, '').trim();
  return cleaned || str; // Return cleaned version or original if no match
};

const PurchaseOrderEmailComposer = ({
  purchaseOrderId,
  poNumber,
  vendorEmail,
  epcsGenerated,
  onClose,
  onSuccess
}) => {
  const normalizedPONumber = normalizePONumber(poNumber);
  const [message, setMessage] = useState('');
  const [cc, setCc] = useState('');
  const [excludeEpc, setExcludeEpc] = useState(!epcsGenerated);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [loadingThreadInfo, setLoadingThreadInfo] = useState(true);
  const [loadingInstructions, setLoadingInstructions] = useState(true);

  // Fetch purchase order data and instructions
  useEffect(() => {
    const fetchPODataAndInstructions = async () => {
      if (!purchaseOrderId) {
        setLoadingInstructions(false);
        return;
      }

      try {
        // Fetch purchase order to get product group IDs
        const poResponse = await axios.get(`/api/v1/management/purchase-order/${purchaseOrderId}`);
        const poData = poResponse.data;
        
        if (!poData?.po_line_items) {
          setLoadingInstructions(false);
          return;
        }

        // Extract unique group IDs from line items
        const groupIds = Array.from(
          new Set(
            poData.po_line_items
              .map(item => item.groupId)
              .filter(Boolean)
          )
        );

        if (groupIds.length === 0) {
          setLoadingInstructions(false);
          return;
        }

        // Fetch product group names and instructions for each group ID
        const groupDataPromises = groupIds.map(async (groupId) => {
          try {
            const [groupResponse, instructionsResponse] = await Promise.all([
              axios.get(`/api/v1/group-data-by-id`, { params: { id: groupId } }).catch(() => ({ data: { item: { name: null } } })),
              axios.get(`/api/v1/instructions/${groupId}`).catch(() => ({ data: { data: [] } }))
            ]);
            
            return {
              groupId,
              groupName: groupResponse.data?.item?.name || null,
              instructions: instructionsResponse.data?.data || []
            };
          } catch (err) {
            console.debug(`Error fetching data for group ${groupId}:`, err);
            return { groupId, groupName: null, instructions: [] };
          }
        });

        const groupDataResults = await Promise.all(groupDataPromises);
        
        // Filter to only groups with instructions
        const groupsWithInstructions = groupDataResults.filter(result => result.instructions.length > 0);

        // Build default message with instructions grouped by product group
        let defaultMessage = `Please see the attached files for PO#${normalizedPONumber}.`;
        
        if (groupsWithInstructions.length > 0) {
          defaultMessage += '\n\nPacking Instructions:\n';
          groupsWithInstructions.forEach((groupData) => {
            if (groupData.groupName) {
              defaultMessage += `\n${groupData.groupName}:\n`;
            }
            groupData.instructions.forEach((instruction) => {
              defaultMessage += `${instruction.description}\n`;
            });
          });
        }

        setMessage(defaultMessage);
      } catch (err) {
        console.error('Error fetching PO data or instructions:', err);
        // Fallback to basic message if fetching fails
        setMessage(`Please see the attached files for PO#${normalizedPONumber}.`);
      } finally {
        setLoadingInstructions(false);
      }
    };

    fetchPODataAndInstructions();
  }, [purchaseOrderId, normalizedPONumber]);

  // Fetch thread CC info on component mount
  useEffect(() => {
    const fetchThreadInfo = async () => {
      if (!purchaseOrderId) {
        setLoadingThreadInfo(false);
        return;
      }
      
      try {
        const response = await axios.get(`/api/v1/management/purchase-order/${purchaseOrderId}/email-thread-info`);
        if (response.data?.cc) {
          setCc(response.data.cc);
        }
      } catch (err) {
        // Silently fail - if no thread exists, that's fine
        console.debug('No thread info found or error fetching:', err);
      } finally {
        setLoadingThreadInfo(false);
      }
    };

    fetchThreadInfo();
  }, [purchaseOrderId]);

  const subject = `Kings Loot - Purchase Order #PO ${normalizedPONumber}`;
  const fromEmail = 'finance@kingsloot.com';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }
    if (!vendorEmail) {
      setError('Vendor email is required');
      return;
    }

    setSending(true);
    setError(null);

    try {
      await axios.post(`/api/v1/management/purchase-order/${purchaseOrderId}/send-email`, {
        message: message.trim(),
        excludeEpc,
        cc: cc.trim() || undefined
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Send Email</h3>
                <button
                  type="button"
                  className="h-8 w-8"
                  onClick={onClose}
                  disabled={sending}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="from" className="block text-sm font-medium text-gray-700">
                    From
                  </label>
                  <input
                    type="email"
                    id="from"
                    value={fromEmail}
                    disabled
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 text-gray-900 text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="to" className="block text-sm font-medium text-gray-700">
                    To
                  </label>
                  <input
                    type="email"
                    id="to"
                    value={vendorEmail || ''}
                    disabled
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 text-gray-900 text-sm"
                  />
                </div>

                <div>
                  <div className="flex items-center gap-2">
                    <label htmlFor="cc" className="block text-sm font-medium text-gray-700">
                        <span className="text-gray-500 font-normal mr-1">
                          CC
                        </span>
                        <span className="text-gray-500 font-normal">(optional, comma-separated)</span> 
                    </label>
                    <InformationCircleIcon
                      className="h-4 w-4 text-primary cursor-pointer outline-none"
                      data-tip={true}
                      data-for="po-email-cc-tooltip"
                      role="button"
                      tabIndex={0}
                      aria-label="How CC is prefilled"
                    />
                    <ReactTooltip
                      id="po-email-cc-tooltip"
                      effect="solid"
                      place="top"
                      event="click"
                      globalEventOff="click"
                      clickable
                      className="max-w-xs text-xs normal-case"
                    >
                      <span className="block text-left">
                        On the first email for this PO, CC is prefilled from your default vendor emails in{' '}
                        <Link
                          to={ROUTES.VENDORS}
                          className="underline font-medium"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          Vendor Settings
                        </Link>
                        . After that, we reuse the CC from the first sent email in this PO thread.
                      </span>
                    </ReactTooltip>
                  </div>
                  <input
                    type="text"
                    id="cc"
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                    placeholder="email1@example.com, email2@example.com"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    value={subject}
                    disabled
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 text-gray-900 text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                    Message
                  </label>
                  <textarea
                    id="message"
                    rows={6}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary focus:border-primary text-sm"
                    placeholder="Enter your message here..."
                    required
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="excludeEpc"
                    checked={excludeEpc}
                    onChange={(e) => setExcludeEpc(e.target.checked)}
                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    disabled={!epcsGenerated}
                  />
                  <label htmlFor="excludeEpc" className="ml-2 block text-sm text-gray-700">
                    Exclude EPC
                    {!epcsGenerated && (
                      <span className="text-gray-500 ml-1">(EPCs have not been generated)</span>
                    )}
                  </label>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={sending || !message.trim()}
                    className="w-full disabled:bg-slate-200 inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {sending ? (
                      <span className="flex items-center">
                        <LoaderSpinner width="16" height="16" border="2" colorSpinner="white" />
                        <span className="ml-2">Sending...</span>
                      </span>
                    ) : (
                      'Send Email'
                    )}
                  </button>
                  <button
                    type="button"
                    disabled={sending}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderEmailComposer;

